/**
 * Reusable ModeCard component for landing page
 */

import { cn } from "../../lib/utils";
import { focusStyles, hoverStyles } from "../../utils/styles";
import { ModeOption } from "../../types/landing";

interface ModeCardProps {
  option: ModeOption;
  isSelected: boolean;
  onClick: () => void;
  theme: {
    colors: {
      text: {
        title: string;
        primary: string;
      };
    };
  };
}

export function ModeCard({ option, isSelected, onClick, theme }: ModeCardProps) {
  const getBorderColor = () => {
    if (isSelected) {
      return `border-brand-secondary bg-surface-muted`;
    }
    return `border-border-default bg-surface-alt hover:border-brand-secondary`;
  };

  // Generate icon rotation angle for each mode
  const getIconRotation = () => {
    const rotations = {
      explorer: "rotate-[50deg]",
      adult: "rotate-[-40deg] scale-y-[-1]",
      admin: "rotate-[290deg]",
    };
    return rotations[option.value] || "rotate-0";
  };

  return (
    <div
      className={cn(
        "xs:px-3 xs:pb-3 px-6 pb-6 pt-8 rounded-lg cursor-pointer border-2 relative",
        getBorderColor(),
        focusStyles.ring,
        hoverStyles.scale
      )}
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label={`Select ${option.label} mode: ${option.description}`}
      onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") {
          onClick();
        }
      }}
      aria-pressed={isSelected}
    >
      {/* Icon inside card but overlapping text */}
      <div className="absolute top-2 sm:-top-3 left-0 sm:left-1/2 transform sm:-translate-x-1/2 z-10 mt-1 ml-6 sm:ml-0">
        {option.icon.startsWith("/") ? (
          <img
            src={option.icon}
            alt={`${option.label} icon`}
            className={cn("w-28 h-28 sm:w-40 sm:h-40 object-contain", getIconRotation())}
          />
        ) : (
          <span className={cn("text-6xl sm:text-8xl", getIconRotation())}>{option.icon}</span>
        )}
      </div>

      {/* Text content that gets overlapped by icon */}
      <div className="pt-20 pl-0 sm:pl-0">
        <h3 className={cn("text-xl font-bold", theme.colors.text.title)}>{option.label}</h3>
        <p className={cn("text-base", theme.colors.text.primary)}>{option.description}</p>
      </div>
    </div>
  );
}
